import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../../../context/ThemeContext";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../../../../store/admin/vehiclesSocketStore";
import { useOpenChat } from "../../../../../pages/support/hooks/useOpenChat";
import NewConversationModal from "../../../../../pages/support/components/NewConversationModal";
import { collectTripParticipantSeed } from "../../../../../pages/support/utils/tripChatHelpers";
import { useTripForm } from "./hooks";
import type { Trip } from "./types";
import {
  TripHeader,
  TripFilters,
  TripList,
  TripPagination,
  TripAddModal,
  TripEditModal,
  TripDetailsModal,
} from "./components";

export default function TripsSection() {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();

  // Estado del modal de chat de viaje. Vive aquí (no dentro de
  // `TripGridCard`) porque al abrir el modal, `NewConversationModal` dispara
  // `useTripsStore.fetchTrips({ limit: 100 })` para poblar su <select> de
  // viajes; eso pone el store en `loading: true`, y `TripList` reacciona con
  // un spinner full-page que desmonta TODAS las cards. Si el modal viviera
  // dentro de la card se desmontaba con ella y "parpadeaba" cerrándose. En
  // este nivel el modal queda fuera del subárbol que se desmonta.
  const [chatTrip, setChatTrip] = useState<Trip | null>(null);
  const socketVehicles = useVehiclesSocketStore((state) => state.vehicles);
  // Combinamos backend (`assignment.driver.user_id`/`email`) + realtime
  // (`vehiclesSocketStore.driver.email`). El modal hace dedupe por user.id.
  const chatParticipantSeed = useMemo(
    () => collectTripParticipantSeed(chatTrip, socketVehicles),
    [chatTrip, socketVehicles],
  );
  const { goToConversation } = useOpenChat();

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const {
    // Data
    trips,
    meta,
    loading,
    selectedTrip,
    loadingSubmit,

    // Form
    register,
    errors,
    setValue,
    watchedValues,

    // Modal states
    showDetailsModal,
    showEditModal,
    showCreateModal,

    // Filter states
    selectedStatus,
    searchQuery,
    selectedDeviceId,
    dateFrom,
    dateTo,
    selectedView,
    currentPage,

    // Handlers
    handleEdit,
    handleDetails,
    handleDelete,
    handleResetTrip,
    handleOpenCreateModal,
    handleCloseModals,
    handleEditSubmit,
    handleCreateSubmit,
    handleStatusFilterChange,
    handleSearchChange,
    handleDeviceFilterChange,
    handleDateFromChange,
    handleDateToChange,
    handleClearFilters,
    setSelectedView,
    setCurrentPage,
  } = useTripForm();

  return (
    <div className="h-full flex flex-col overflow-y-auto overflow-x-hidden">
      <TripHeader
        themeClasses={themeClasses}
        onCreateTrip={handleOpenCreateModal}
        t={t}
      />

      <TripFilters
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        selectedStatus={selectedStatus}
        searchQuery={searchQuery}
        selectedDeviceId={selectedDeviceId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onStatusChange={handleStatusFilterChange}
        onSearchChange={handleSearchChange}
        onDeviceChange={handleDeviceFilterChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        onClearFilters={handleClearFilters}
        onViewChange={setSelectedView}
        selectedView={selectedView}
        t={t}
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <TripList
          trips={trips as Trip[]}
          themeClasses={themeClasses}
          onEdit={handleEdit}
          onDetails={handleDetails}
          onDelete={handleDelete}
          onOpenChat={setChatTrip}
          selectedView={selectedView}
          t={t}
          loading={loading}
          disableCRUD={disableCRUD}
        />

        {!loading && trips.length > 0 && (
          <TripPagination
            themeClasses={themeClasses}
            currentPage={currentPage}
            totalPages={meta?.totalPages ?? 1}
            totalItems={meta?.totalItems ?? 0}
            onPageChange={setCurrentPage}
            t={t}
          />
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <TripAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleCreateSubmit}
          t={t}
          setValue={setValue}
          watchedValues={watchedValues}
        />
      )}

      {showEditModal && selectedTrip && (
        <TripEditModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleEditSubmit}
          onReset={() => handleResetTrip(selectedTrip)}
          t={t}
          setValue={setValue}
          watchedValues={watchedValues}
          tripStatus={selectedTrip.status?.name}
          selectedTrip={selectedTrip}
        />
      )}

      {showDetailsModal && selectedTrip && (
        <TripDetailsModal
          trip={selectedTrip}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={handleCloseModals}
          onEdit={() => {
            handleCloseModals();
            handleEdit(selectedTrip);
          }}
          t={t}
          disableCRUD={disableCRUD}
        />
      )}

      {chatTrip && (
        <NewConversationModal
          initialMode="viaje"
          initialTripId={chatTrip.id}
          initialParticipantEmails={chatParticipantSeed.emails}
          initialParticipantUserIds={chatParticipantSeed.userIds}
          onClose={() => setChatTrip(null)}
          onCreated={(conversationId) => {
            setChatTrip(null);
            goToConversation(conversationId);
          }}
        />
      )}
    </div>
  );
}
